import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Stack, Text, TextInput } from "@mantine/core";
import { toast } from "sonner";
import { tenantApi } from "@/lib/api/client";
import { NETWORK_DEVICES_QUERY_KEY } from "@/hooks/use-network-queries";

const EMPTY = { ip_address: "", mac_address: "", hostname: "", comment: "" };

/**
 * Add a static address by hand (company admins) — for a device that isn't in
 * the devices list yet. The Devices page's "Add static address" dialog, also
 * the `network.add_device` guide block (#436). `onAdded` fires once the
 * address is saved.
 */
export function AddStaticLeaseForm({
  pool,
  onAdded,
}: {
  pool: { start: string; end: string } | null;
  onAdded?: () => void;
}) {
  const queryClient = useQueryClient();
  const [newLease, setNewLease] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const handleAddStatic = async () => {
    setSaving(true);
    try {
      await tenantApi.createStaticLease(newLease);
      toast.success(`Made ${newLease.ip_address} static`);
      setNewLease(EMPTY);
      queryClient.invalidateQueries({ queryKey: NETWORK_DEVICES_QUERY_KEY });
      onAdded?.();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || "Failed to make the address static");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="sm">
      {pool && (
        <Text fz="xs" c="dimmed">
          DHCP pool: {pool.start} – {pool.end}. An address inside the pool works if it is free.
        </Text>
      )}
      <TextInput
        placeholder="IP Address (e.g. 10.97.1.25)"
        value={newLease.ip_address}
        onChange={(e) => setNewLease((p) => ({ ...p, ip_address: e.target.value }))}
      />
      <TextInput
        placeholder="MAC Address (e.g. AA:BB:CC:DD:EE:FF)"
        value={newLease.mac_address}
        onChange={(e) => setNewLease((p) => ({ ...p, mac_address: e.target.value }))}
      />
      <TextInput
        placeholder="Hostname (optional)"
        value={newLease.hostname}
        onChange={(e) => setNewLease((p) => ({ ...p, hostname: e.target.value }))}
      />
      <TextInput
        placeholder="Comment (optional)"
        value={newLease.comment}
        onChange={(e) => setNewLease((p) => ({ ...p, comment: e.target.value }))}
      />
      <Button
        fullWidth
        loading={saving}
        onClick={handleAddStatic}
        disabled={!newLease.ip_address || !newLease.mac_address}
      >
        Make static
      </Button>
    </Stack>
  );
}
